/**
 * VersionHistoryModal Component (UI-4)
 * Modal for viewing and restoring prompt version history
 *
 * Features:
 * - Version table: Version#, Date, Changed By, Reason, Actions
 * - Current version indicator (star)
 * - View button: Show version content
 * - Restore button: Confirm dialog with reason field
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Tooltip,
    Alert,
    TextField,
    Skeleton,
    Paper,
    Chip,
    Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestoreIcon from '@mui/icons-material/Restore';
import StarIcon from '@mui/icons-material/Star';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { toast } from 'react-toastify';
import * as Diff from 'diff';

import { getPromptVersions, restorePromptVersion } from '../../api/promptsApi';

// Embedded theme
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539', quaternary: '#3f4147' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.85)', quaternary: 'rgba(255,255,255,0.75)', placeholder: 'rgba(255,255,255,0.6)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)', tertiary: 'rgba(255,255,255,0.15)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    borderRadius: { sm: '8px', md: '12px' }
};

const VersionHistoryModal = ({ open, prompt, onClose, onRestore }) => {
    // State
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [viewExpanded, setViewExpanded] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [restoreReason, setRestoreReason] = useState('');
    const [restoring, setRestoring] = useState(false);

    // Diff comparison state
    const [compareMode, setCompareMode] = useState(false);
    const [compareVersionA, setCompareVersionA] = useState(null);
    const [compareVersionB, setCompareVersionB] = useState(null);

    // ─────────────────────────────────────────────────────────────────────────
    // Fetch versions
    // ─────────────────────────────────────────────────────────────────────────

    const fetchVersions = useCallback(async () => {
        if (!prompt?.promptKey) return;

        setLoading(true);
        setError(null);

        const result = await getPromptVersions(prompt.promptKey);

        if (result.success) {
            // Sort by version descending (newest first)
            const sorted = [...result.data].sort((a, b) => b.version - a.version);
            setVersions(sorted);
        } else {
            setError(result.error);
        }

        setLoading(false);
    }, [prompt?.promptKey]);

    useEffect(() => {
        if (open && prompt) {
            fetchVersions();
            setSelectedVersion(null);
            setViewExpanded(false);
            setCompareMode(false);
            setCompareVersionA(null);
            setCompareVersionB(null);
        }
    }, [open, prompt, fetchVersions]);

    // ─────────────────────────────────────────────────────────────────────────
    // View version
    // ─────────────────────────────────────────────────────────────────────────

    const handleView = (version) => {
        if (selectedVersion?.version === version.version) {
            setViewExpanded(!viewExpanded);
        } else {
            setSelectedVersion(version);
            setViewExpanded(true);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Restore version
    // ─────────────────────────────────────────────────────────────────────────

    const handleRestoreClick = (version) => {
        setSelectedVersion(version);
        setRestoreReason('');
        setRestoreDialogOpen(true);
    };

    const handleRestoreConfirm = async () => {
        if (!selectedVersion) return;

        setRestoring(true);
        const result = await restorePromptVersion(
            prompt.promptKey,
            selectedVersion.version,
            restoreReason.trim() || `Restored from v${selectedVersion.version}`
        );
        setRestoring(false);

        if (result.success) {
            toast.success(`Restored to version ${selectedVersion.version}`);
            setRestoreDialogOpen(false);
            onRestore?.();
        } else {
            toast.error(result.error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Compare versions
    // ─────────────────────────────────────────────────────────────────────────

    const handleCompareClick = (version) => {
        if (!compareMode) {
            // Start compare mode, set first version
            setCompareMode(true);
            setCompareVersionA(version);
            setCompareVersionB(null);
            setViewExpanded(false);
        } else if (!compareVersionB && version.version !== compareVersionA?.version) {
            // Set second version
            setCompareVersionB(version);
        } else if (version.version === compareVersionA?.version) {
            // Clicked on already selected A, deselect
            setCompareVersionA(null);
            setCompareMode(false);
        } else {
            // Replace version B
            setCompareVersionB(version);
        }
    };

    const exitCompareMode = () => {
        setCompareMode(false);
        setCompareVersionA(null);
        setCompareVersionB(null);
    };

    const renderDiff = () => {
        if (!compareVersionA || !compareVersionB) return null;

        const textA = compareVersionA.promptText || '';
        const textB = compareVersionB.promptText || '';
        const diff = Diff.diffLines(textA, textB);

        return (
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                {/* Left side - Version A (older) */}
                <Paper
                    sx={{
                        flex: 1,
                        background: theme.colors.bg.tertiary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: theme.borderRadius.sm,
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        sx={{
                            p: 1.5,
                            background: `${theme.colors.status.error}15`,
                            borderBottom: `1px solid ${theme.colors.border.primary}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Typography sx={{ color: theme.colors.status.error, fontWeight: 600, fontSize: '0.85rem' }}>
                            v{compareVersionA.version} (Old)
                        </Typography>
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                            {formatDate(compareVersionA.createdAt)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            p: 1.5,
                            maxHeight: 300,
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            lineHeight: 1.6
                        }}
                    >
                        {diff.map((part, i) => (
                            <Box
                                key={i}
                                component="span"
                                sx={{
                                    display: 'block',
                                    background: part.added
                                        ? 'transparent'
                                        : part.removed
                                            ? `${theme.colors.status.error}20`
                                            : 'transparent',
                                    color: part.added
                                        ? 'transparent'
                                        : part.removed
                                            ? theme.colors.status.error
                                            : theme.colors.text.tertiary,
                                    textDecoration: part.removed ? 'line-through' : 'none',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    minHeight: part.added ? `${part.value.split('\n').length * 1.6}em` : 'auto'
                                }}
                            >
                                {part.added ? '' : part.value}
                            </Box>
                        ))}
                    </Box>
                </Paper>

                {/* Right side - Version B (newer) */}
                <Paper
                    sx={{
                        flex: 1,
                        background: theme.colors.bg.tertiary,
                        border: `1px solid ${theme.colors.border.primary}`,
                        borderRadius: theme.borderRadius.sm,
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        sx={{
                            p: 1.5,
                            background: `${theme.colors.status.success}15`,
                            borderBottom: `1px solid ${theme.colors.border.primary}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Typography sx={{ color: theme.colors.status.success, fontWeight: 600, fontSize: '0.85rem' }}>
                            v{compareVersionB.version} (New)
                        </Typography>
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                            {formatDate(compareVersionB.createdAt)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            p: 1.5,
                            maxHeight: 300,
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            lineHeight: 1.6
                        }}
                    >
                        {diff.map((part, i) => (
                            <Box
                                key={i}
                                component="span"
                                sx={{
                                    display: 'block',
                                    background: part.removed
                                        ? 'transparent'
                                        : part.added
                                            ? `${theme.colors.status.success}20`
                                            : 'transparent',
                                    color: part.removed
                                        ? 'transparent'
                                        : part.added
                                            ? theme.colors.status.success
                                            : theme.colors.text.tertiary,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    minHeight: part.removed ? `${part.value.split('\n').length * 1.6}em` : 'auto'
                                }}
                            >
                                {part.removed ? '' : part.value}
                            </Box>
                        ))}
                    </Box>
                </Paper>
            </Box>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Format date
    // ─────────────────────────────────────────────────────────────────────────

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────

    const currentVersion = prompt?.version;

    return (
        <>
            <Dialog
                open={open}
                onClose={loading ? undefined : onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.border.primary}`,
                        maxHeight: '85vh'
                    }
                }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.colors.border.primary}`,
                        pb: 2
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: theme.borderRadius.sm,
                                background: `${theme.colors.status.warning}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <HistoryIcon sx={{ color: theme.colors.status.warning }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, fontSize: '1.1rem' }}>
                                Version History
                            </Typography>
                            <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                {prompt?.promptName || 'Unknown'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: theme.colors.text.quaternary }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                {/* Content */}
                <DialogContent sx={{ pt: 3 }}>
                    {/* Error */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2, background: `${theme.colors.status.error}20`, color: theme.colors.status.error }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Version Table */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            background: theme.colors.bg.tertiary,
                            border: `1px solid ${theme.colors.border.primary}`,
                            borderRadius: theme.borderRadius.sm,
                            mb: 2
                        }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }}>
                                        Version
                                    </TableCell>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }}>
                                        Date
                                    </TableCell>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }}>
                                        Changed By
                                    </TableCell>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }}>
                                        Reason
                                    </TableCell>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderColor: theme.colors.border.primary }} align="right">
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [...Array(3)].map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                                <Skeleton variant="text" width={40} sx={{ bgcolor: theme.colors.bg.quaternary }} />
                                            </TableCell>
                                            <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                                <Skeleton variant="text" width={120} sx={{ bgcolor: theme.colors.bg.quaternary }} />
                                            </TableCell>
                                            <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                                <Skeleton variant="text" width={100} sx={{ bgcolor: theme.colors.bg.quaternary }} />
                                            </TableCell>
                                            <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                                <Skeleton variant="text" width={150} sx={{ bgcolor: theme.colors.bg.quaternary }} />
                                            </TableCell>
                                            <TableCell sx={{ borderColor: theme.colors.border.primary }} align="right">
                                                <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: theme.colors.bg.quaternary, ml: 'auto' }} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : versions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ borderColor: theme.colors.border.primary, textAlign: 'center', py: 4 }}>
                                            <Typography sx={{ color: theme.colors.text.quaternary }}>
                                                No version history available
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    versions.map((version) => {
                                        const isCurrent = version.version === currentVersion;
                                        return (
                                            <TableRow
                                                key={version.version}
                                                sx={{
                                                    background: isCurrent ? `${theme.colors.status.success}08` : 'transparent',
                                                    '&:hover': { background: theme.colors.bg.quaternary }
                                                }}
                                            >
                                                <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography sx={{ color: theme.colors.text.primary, fontWeight: 500 }}>
                                                            v{version.version}
                                                        </Typography>
                                                        {isCurrent && (
                                                            <Tooltip title="Current version">
                                                                <StarIcon sx={{ fontSize: 16, color: theme.colors.status.warning }} />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                                    <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.85rem' }}>
                                                        {formatDate(version.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                                    <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.85rem' }}>
                                                        {version.changedBy || 'System'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderColor: theme.colors.border.primary }}>
                                                    <Typography
                                                        sx={{
                                                            color: theme.colors.text.quaternary,
                                                            fontSize: '0.85rem',
                                                            maxWidth: 200,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {version.changeReason || 'Initial version'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderColor: theme.colors.border.primary }} align="right">
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                        <Tooltip title="View content">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleView(version)}
                                                                disabled={compareMode}
                                                                sx={{
                                                                    color: selectedVersion?.version === version.version && viewExpanded
                                                                        ? theme.colors.accent.tertiary
                                                                        : theme.colors.text.tertiary,
                                                                    '&:hover': { color: theme.colors.accent.tertiary }
                                                                }}
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={
                                                            compareMode
                                                                ? compareVersionA?.version === version.version
                                                                    ? 'Selected (click to cancel)'
                                                                    : compareVersionB?.version === version.version
                                                                        ? 'Comparing with'
                                                                        : 'Compare with this'
                                                                : 'Compare versions'
                                                        }>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleCompareClick(version)}
                                                                sx={{
                                                                    color: compareVersionA?.version === version.version
                                                                        ? theme.colors.status.error
                                                                        : compareVersionB?.version === version.version
                                                                            ? theme.colors.status.success
                                                                            : theme.colors.text.tertiary,
                                                                    '&:hover': { color: theme.colors.status.info }
                                                                }}
                                                            >
                                                                <CompareArrowsIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {!isCurrent && (
                                                            <Tooltip title="Restore this version">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleRestoreClick(version)}
                                                                    disabled={compareMode}
                                                                    sx={{ color: theme.colors.text.tertiary, '&:hover': { color: theme.colors.status.warning } }}
                                                                >
                                                                    <RestoreIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Compare Mode Info Bar */}
                    {compareMode && (
                        <Paper
                            sx={{
                                p: 1.5,
                                mb: 2,
                                background: `${theme.colors.status.info}15`,
                                border: `1px solid ${theme.colors.status.info}40`,
                                borderRadius: theme.borderRadius.sm,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CompareArrowsIcon sx={{ color: theme.colors.status.info }} />
                                <Typography sx={{ color: theme.colors.status.info, fontSize: '0.875rem' }}>
                                    {!compareVersionB
                                        ? `Selected v${compareVersionA?.version} — Click another version to compare`
                                        : `Comparing v${compareVersionA?.version} with v${compareVersionB?.version}`
                                    }
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                onClick={exitCompareMode}
                                sx={{ color: theme.colors.status.info }}
                            >
                                Cancel
                            </Button>
                        </Paper>
                    )}

                    {/* Side-by-Side Diff View */}
                    {compareMode && compareVersionA && compareVersionB && renderDiff()}

                    {/* Selected Version Preview (only when not comparing) */}
                    <Collapse in={viewExpanded && selectedVersion !== null && !compareMode}>
                        <Paper
                            sx={{
                                background: theme.colors.bg.tertiary,
                                border: `1px solid ${theme.colors.border.primary}`,
                                borderRadius: theme.borderRadius.sm,
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    background: theme.colors.bg.quaternary,
                                    borderBottom: `1px solid ${theme.colors.border.primary}`
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, fontSize: '0.875rem' }}>
                                        Version {selectedVersion?.version} Content
                                    </Typography>
                                    {selectedVersion?.version === currentVersion && (
                                        <Chip
                                            label="Current"
                                            size="small"
                                            sx={{
                                                background: `${theme.colors.status.success}20`,
                                                color: theme.colors.status.success,
                                                height: 20,
                                                fontSize: '0.7rem'
                                            }}
                                        />
                                    )}
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => setViewExpanded(false)}
                                    sx={{ color: theme.colors.text.quaternary }}
                                >
                                    <ExpandLessIcon />
                                </IconButton>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    maxHeight: 250,
                                    overflow: 'auto',
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    color: theme.colors.text.tertiary,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.6
                                }}
                            >
                                {selectedVersion?.promptText || 'No content available'}
                            </Box>
                        </Paper>
                    </Collapse>

                    {/* Legend */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ fontSize: 14, color: theme.colors.status.warning }} />
                            <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                = Current version
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>

                {/* Actions */}
                <DialogActions
                    sx={{
                        borderTop: `1px solid ${theme.colors.border.primary}`,
                        p: 2
                    }}
                >
                    <Button
                        onClick={onClose}
                        sx={{ color: theme.colors.text.tertiary }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Restore Confirmation Dialog */}
            <Dialog
                open={restoreDialogOpen}
                onClose={() => !restoring && setRestoreDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.colors.bg.secondary,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.border.primary}`
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RestoreIcon sx={{ color: theme.colors.status.warning }} />
                        <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                            Restore Version {selectedVersion?.version}?
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Alert
                        severity="warning"
                        sx={{ mb: 2, background: `${theme.colors.status.warning}20`, color: theme.colors.status.warning }}
                    >
                        This will create a new version (v{currentVersion + 1}) with the content from v{selectedVersion?.version}.
                    </Alert>
                    <TextField
                        label="Restore Reason (optional)"
                        value={restoreReason}
                        onChange={(e) => setRestoreReason(e.target.value)}
                        fullWidth
                        placeholder={`Restored from v${selectedVersion?.version}`}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                background: theme.colors.bg.tertiary,
                                color: theme.colors.text.primary,
                                '& fieldset': { borderColor: theme.colors.border.primary },
                                '&:hover fieldset': { borderColor: theme.colors.border.tertiary },
                                '&.Mui-focused fieldset': { borderColor: theme.colors.accent.primary }
                            },
                            '& .MuiInputLabel-root': { color: theme.colors.text.quaternary }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2 }}>
                    <Button
                        onClick={() => setRestoreDialogOpen(false)}
                        disabled={restoring}
                        sx={{ color: theme.colors.text.tertiary }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleRestoreConfirm}
                        disabled={restoring}
                        sx={{
                            background: theme.colors.status.warning,
                            '&:hover': { background: '#d97706' }
                        }}
                    >
                        {restoring ? 'Restoring...' : 'Confirm Restore'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VersionHistoryModal;
